import React, { useEffect, useMemo, useState } from "react"
import { TopNavTemplate } from "templates/TopNavTemplate"
import styled from "styled-components"
import { WelcomeBackCard } from "pages/Dashboard/WelcomeBackCard"
import { ApplicationSummaryCard } from "pages/Dashboard/ApplicationSummaryCard"
import { useRequireAuthentication } from "hooks/useRequireAuthentication"
import { useApplicationSummaries } from "hooks/useApplicationSummaries"
import devices from "styles/devices"
import { useTrackNavigatedToPage } from "hooks/useTrackPageView"
import { LogGaEvent } from "lib/GoogleAnalytics"
import { EventAction, EventCategory } from "models/GoogleAnalytics"
import { GetActiveApplications } from "lib/Applications"
import { ActiveApplicationsModal } from "components/molecules/ActiveApplicationsModal"
import { useLocation } from "react-router-dom"
import { redirectToDiscovery } from "lib/discovery"

const Layout = styled.div`
  display: grid;
  height: 100%;
  grid-gap: 1.6rem;
  margin-top: -110px;

  @media ${devices.bp_md} {
    grid-template-columns: 340px 1fr;
    grid-column-gap: 1.75rem;
    margin-top: 0;
  }

  @media ${devices.bp_xl} {
    grid-template-columns: 340px minmax(auto, 650px);
    grid-column-gap: 3.1875rem;
    margin-top: 0;
  }
`

const WelcomeBackStickyArea = styled.div`
  position: relative;
  display: block;
  margin-top: 8rem;
  @media ${devices.bp_md} {
    position: sticky;
    top: 190px;
    margin-top: 0;
    height: 280px;
  }
`

const ApplicationsList = styled.div``

const AppCardWrapper = styled.div`
  margin-bottom: 20px;
`

const MobileTitle = styled.h2`
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
  margin-top: 2rem;
  color: #434344;
  @media ${devices.bp_md} {
    display: none;
  }
`

export default function () {
  useRequireAuthentication(false)
  useTrackNavigatedToPage("/dashboard")

  const { applicationSummaries, hasFetchedSummaries } = useApplicationSummaries()
  const [loggedOpps, setLoggedOpps] = useState<boolean>(false)
  const [showReturningUserModal, setShowReturningUserModal] = useState<boolean>(true)
  const [showStartNewAppModal, setShowStartNewAppModal] = useState<boolean>(false)
  const query = new URLSearchParams(useLocation().search)
  const isReturningUser = query.get("returningUser") === "true"

  useEffect(() => {
    LogGaEvent({
      action: EventAction.pageViewed,
      event_category: EventCategory.formInteraction,
      event_label: "MyMojo - Dashboard",
    })
  }, [])

  useEffect(() => {
    if (hasFetchedSummaries && !loggedOpps) {
      LogGaEvent({
        action: EventAction.dashboardOpps,
        event_category: EventCategory.customEvent,
        event_label: `${applicationSummaries.length}`,
      })
      setLoggedOpps(true)
    }
  }, [hasFetchedSummaries])

  const activeApplications = useMemo(
    () => GetActiveApplications(applicationSummaries ?? []),
    [applicationSummaries]
  )

  const activeCount = activeApplications.length
  const isReturningUserFlow = isReturningUser && activeCount > 0 && showReturningUserModal

  const onCloseModal = () => {
    document.querySelector("html").style.overflow = "auto"
    setShowReturningUserModal(false)
    setShowStartNewAppModal(false)
  }

  const handleStartNewApplication = () => {
    if (!isReturningUser && activeCount > 0) {
      setShowStartNewAppModal(true)
    } else {
      redirectToDiscovery()
    }
  }

  const getModalConfig = () => {
    const hasSingleApp = activeCount === 1
    const isStartNewFlow = showStartNewAppModal

    if (isReturningUserFlow) {
      return {
        heading: "Welcome back",
        paragraphs: ["Let's pick up from where you left off."],
        buttonText: hasSingleApp ? "View application" : "View applications",
        buttonAction: "view-application" as const,
        showApplicationCard: hasSingleApp,
        application: hasSingleApp ? activeApplications[0] : undefined,
      }
    }

    if (isStartNewFlow) {
      return {
        heading: "Start a new application",
        paragraphs: [
          hasSingleApp
            ? "You have an active application. To make changes, discuss or proceed with this application, close this window and return to your account."
            : "You have active applications. To make changes, discuss or proceed with an application, close this window and return to your account.",
          "Otherwise, start a new application below.",
        ],
        buttonText: "Start new application",
        buttonAction: "start-new-application" as const,
        subtext:
          "If you start a brand new application this will not be linked to any active case, you will need to provide your details again and you may be allocated to a different expert should you look to get advice and apply.",
        showApplicationCard: hasSingleApp,
        application: hasSingleApp ? activeApplications[0] : undefined,
      }
    }

    return null
  }

  return (
    <TopNavTemplate
      content={
        <Layout>
          <WelcomeBackStickyArea>
            <WelcomeBackCard onStartNewApplication={handleStartNewApplication} />
          </WelcomeBackStickyArea>

          <ApplicationsList>
            {applicationSummaries?.length > 0 && <MobileTitle>Your applications</MobileTitle>}
            {applicationSummaries?.map((summary) => {
              return (
                <AppCardWrapper key={summary.friendlyId}>
                  <ApplicationSummaryCard friendlyId={summary.friendlyId} />
                </AppCardWrapper>
              )
            })}
          </ApplicationsList>
          {(showReturningUserModal || showStartNewAppModal) &&
            (() => {
              const config = getModalConfig()
              return config ? (
                <ActiveApplicationsModal
                  application={config.application}
                  onClose={onCloseModal}
                  config={config}
                />
              ) : null
            })()}
        </Layout>
      }
    />
  )
}
